const MS_PER_SECOND = 1000;

export function clampFrame(frame: number, durationFrames: number): number {
  if (!Number.isFinite(frame)) return 0;
  return Math.min(
    Math.max(Math.round(frame), 0),
    Math.max(0, Math.round(durationFrames)),
  );
}

export function millisecondsToFrames(
  milliseconds: number,
  sampleRate: number,
): number {
  assertSampleRate(sampleRate);
  return Math.round((milliseconds * sampleRate) / MS_PER_SECOND);
}

export function framesToMilliseconds(
  frames: number,
  sampleRate: number,
): number {
  assertSampleRate(sampleRate);
  return Math.round((frames * MS_PER_SECOND) / sampleRate);
}

export function framesToSeconds(frames: number, sampleRate: number): number {
  assertSampleRate(sampleRate);
  return frames / sampleRate;
}

export function secondsToFrames(seconds: number, sampleRate: number): number {
  assertSampleRate(sampleRate);
  return Math.round(seconds * sampleRate);
}

export function formatFrames(frames: number, sampleRate: number): string {
  return formatMilliseconds(framesToMilliseconds(frames, sampleRate));
}

export function formatMilliseconds(totalMilliseconds: number): string {
  const safe = Math.max(0, Math.round(totalMilliseconds));
  const milliseconds = safe % 1000;
  const totalSeconds = Math.floor(safe / 1000);
  const seconds = totalSeconds % 60;
  const totalMinutes = Math.floor(totalSeconds / 60);
  const minutes = totalMinutes % 60;
  const hours = Math.floor(totalMinutes / 60);
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}.${String(milliseconds).padStart(3, "0")}`;
}

export function parseTimeToFrames(
  value: string,
  sampleRate: number,
  durationFrames: number,
): number | null {
  const match = value
    .trim()
    .match(/^(\d+):([0-5]?\d):([0-5]?\d)(?:\.(\d{1,3}))?$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3]);
  const milliseconds = Number((match[4] ?? "").padEnd(3, "0"));
  const totalMs = ((hours * 60 + minutes) * 60 + seconds) * 1000 + milliseconds;
  return clampFrame(millisecondsToFrames(totalMs, sampleRate), durationFrames);
}

export function frameStep(
  milliseconds: 1 | 10 | 100 | 1000,
  sampleRate: number,
): number {
  return Math.max(1, millisecondsToFrames(milliseconds, sampleRate));
}

function assertSampleRate(sampleRate: number): void {
  if (!Number.isFinite(sampleRate) || sampleRate <= 0)
    throw new RangeError("sampleRate must be positive");
}
